import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getAbsoluteUrl } from "../../utils/metaUtils";
import { 
 FaStore, 
 FaChartLine, 
 FaCreditCard, 
 FaPlug, 
 FaShieldAlt, 
 FaRocket,
 FaCheckCircle,
 FaArrowRight
} from "react-icons/fa";

const PlatformLanding: React.FC = () => {
 const features = [
  {
   icon: <FaStore className="text-4xl text-indigo-600" />,
   title: "Multi-Store Management",
   description: "Create and manage unlimited stores from a single platform. Each store gets its own subdomain and branding."
  },
  {
   icon: <FaChartLine className="text-4xl text-indigo-600" />,
   title: "Advanced Analytics",
   description: "Track performance across all stores with comprehensive analytics, revenue reports, and usage statistics."
  },
  {
   icon: <FaCreditCard className="text-4xl text-indigo-600" />,
   title: "Flexible Subscriptions",
   description: "Manage subscriptions, billing, and payments with support for multiple business models and payment gateways."
  },
  {
   icon: <FaPlug className="text-4xl text-indigo-600" />,
   title: "Plugin Ecosystem",
   description: "Extend functionality with a powerful plugin system. Enable or disable features per store as needed."
  },
  {
   icon: <FaShieldAlt className="text-4xl text-indigo-600" />,
   title: "Enterprise Security",
   description: "Built with security in mind. Audit logs, role-based access control, and secure API endpoints."
  },
  {
   icon: <FaRocket className="text-4xl text-indigo-600" />,
   title: "Scalable Infrastructure",
   description: "Handle growth effortlessly. Auto-scaling architecture designed for high traffic and multiple stores."
  }
 ];

 const benefits = [
  "Unlimited stores per platform",
  "Custom subdomains for each store",
  "Store-specific branding and themes",
  "Real-time analytics and reporting",
  "Automated DNS management",
  "Plugin-based architecture",
  "Multi-tenant isolation",
  "24/7 platform monitoring"
 ];

 const siteUrl = getAbsoluteUrl("/");
 const siteName = "Multi-Store E-Commerce Platform";
 const siteDescription = "Launch and manage multiple online stores from a single, powerful platform. Each store gets its own identity, branding, and subdomain.";

 return (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
   <Helmet>
    <title>{siteName}</title>
    <meta name="description" content={siteDescription} />
    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content={siteUrl} />
    <meta property="og:title" content={siteName} />
    <meta property="og:description" content={siteDescription} />
    <meta property="og:site_name" content={siteName} />
    {/* Twitter */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:url" content={siteUrl} />
    <meta name="twitter:title" content={siteName} />
    <meta name="twitter:description" content={siteDescription} />
   </Helmet>
   {/* Hero Section */}
   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
    <div className="text-center">
     <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6">
      <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
       Multi-Store
      </span>
      <br />
      E-Commerce Platform
     </h1>
     <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-3xl mx-auto">
      Launch and manage multiple online stores from a single, powerful platform.
      <br />
      Each store gets its own identity, branding, and subdomain.
     </p>
     <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
      Perfect for agencies, marketplaces, and businesses managing multiple brands.
     </p>
     <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
      <Link
       to="/platform/login"
       className="inline-flex items-center px-8 py-4 bg-blue-600 text-white text-lg font-semibold hover:bg-blue-700 transition-all transform  hover:"
      >
       Access Admin Portal
       <FaArrowRight className="ml-2" />
      </Link>
      <a
       href="#features"
       className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 text-lg font-semibold border-2 border-indigo-600 hover:bg-blue-50 transition-all"
      >
       Learn More
      </a>
     </div>
    </div>
   </div>

   {/* Features Section */}
   <div id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="text-center mb-12">
     <h2 className="text-4xl font-bold text-gray-900 mb-4">Platform Features</h2>
     <p className="text-xl text-gray-600 max-w-2xl mx-auto">
      Everything you need to manage multiple stores efficiently
     </p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
     {features.map((feature, index) => (
      <div
       key={index}
       className="bg-white  p-8 transition-all transform hover:-translate-y-2"
      >
       <div className="mb-4">{feature.icon}</div>
       <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
       <p className="text-gray-600">{feature.description}</p>
      </div>
     ))}
    </div>
   </div>

   {/* Benefits Section */}
   <div className="bg-white py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
     <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
       <h2 className="text-4xl font-bold text-gray-900 mb-6">
        Why Choose Our Platform?
       </h2>
       <p className="text-lg text-gray-600 mb-8">
        Our multi-store platform is designed for businesses that need to manage
        multiple online stores efficiently. Whether you're running a marketplace,
        managing client stores, or operating multiple brands, we've got you covered.
       </p>
       <ul className="space-y-4">
        {benefits.map((benefit, index) => (
         <li key={index} className="flex items-start">
          <FaCheckCircle className="text-green-500 text-xl mr-3 mt-1 flex-shrink-0" />
          <span className="text-gray-700 text-lg">{benefit}</span>
         </li>
        ))}
       </ul>
      </div>
      <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-8">
       <div className="bg-white p-6 ">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Started Today</h3>
        <p className="text-gray-600 mb-6">
         Start managing multiple stores with our powerful platform.
         Create your first store in minutes.
        </p>
        <Link
         to="/platform/login"
         className="inline-block w-full text-center px-6 py-3 bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
        >
         Login to Admin Portal
        </Link>
       </div>
      </div>
     </div>
    </div>
   </div>

   {/* CTA Section */}
   <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
     <h2 className="text-4xl font-bold text-white mb-4">
      Ready to Launch Multiple Stores?
     </h2>
     <p className="text-xl text-indigo-100 mb-8">
      Join our platform and start managing your stores today.
     </p>
     <Link
      to="/platform/login"
      className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 text-lg font-semibold hover:bg-gray-100 transition-all transform "
     >
      Get Started Now
      <FaArrowRight className="ml-2" />
     </Link>
    </div>
   </div>
  </div>
 );
};

export default PlatformLanding;

